import * as RadixSelect from '@radix-ui/react-select';
import clsx from 'clsx';
import { Check, ChevronDown } from 'lucide-react';
import type { ReactNode } from 'react';
import { Icon } from '../display/Icon.tsx';
import { Field } from './Field.tsx';
import inputStyles from './Input.module.css';
import styles from './Select.module.css';

export interface SelectOption<V extends string> {
  readonly value: V;
  readonly label: string;
  /** Secondary line in the list, e.g. a technical detail. */
  readonly detail?: string | undefined;
  readonly disabled?: boolean | undefined;
}

export interface SelectProps<V extends string> {
  readonly label: string;
  readonly labelHidden?: boolean | undefined;
  readonly value: V | null;
  readonly options: readonly SelectOption<V>[];
  readonly onChange: (value: V) => void;
  readonly placeholder?: string | undefined;
  readonly hint?: ReactNode | undefined;
  readonly error?: string | null | undefined;
  readonly disabled?: boolean | undefined;
  readonly className?: string | undefined;
}

/** Choosing one of many. Options come from data (registries, API), never from markup. */
export const Select = <V extends string>({
  label,
  labelHidden,
  value,
  options,
  onChange,
  placeholder,
  hint,
  error,
  disabled,
  className,
}: SelectProps<V>) => (
  <Field label={label} labelHidden={labelHidden} hint={hint} error={error} className={className}>
    {(control) => (
      <RadixSelect.Root
        value={value ?? ''}
        onValueChange={(next) => {
          const option = options.find((candidate) => candidate.value === next);
          if (option) onChange(option.value);
        }}
        disabled={disabled ?? false}
      >
        <RadixSelect.Trigger className={clsx(inputStyles.control, styles.trigger)} {...control}>
          <span className={styles.value}>
            <RadixSelect.Value placeholder={placeholder} />
          </span>
          <RadixSelect.Icon className={styles.chevron}>
            <Icon icon={ChevronDown} size="sm" />
          </RadixSelect.Icon>
        </RadixSelect.Trigger>
        <RadixSelect.Portal>
          <RadixSelect.Content
            className={styles.content}
            position="popper"
            sideOffset={4}
            collisionPadding={8}
          >
            <RadixSelect.Viewport className={styles.viewport}>
              {options.map((option) => (
                <RadixSelect.Item
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled ?? false}
                  className={styles.item}
                >
                  <span className={styles.itemText}>
                    <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
                    {option.detail && <span className={styles.detail}>{option.detail}</span>}
                  </span>
                  <RadixSelect.ItemIndicator className={styles.indicator}>
                    <Icon icon={Check} size="sm" />
                  </RadixSelect.ItemIndicator>
                </RadixSelect.Item>
              ))}
            </RadixSelect.Viewport>
          </RadixSelect.Content>
        </RadixSelect.Portal>
      </RadixSelect.Root>
    )}
  </Field>
);
